import { Assignment, getActiveTimeEntry } from './assignment';
import {
  startTimeTracking as apiStartTimeTracking,
  stopTimeTracking as apiStopTimeTracking,
} from './assignmentService';

/**
 * Start a time tracking session for an assignment
 * @param assignment The assignment to track time for
 * @param userId The ID of the user tracking time
 * @param userName The name of the user tracking time
 * @returns A promise that resolves to the updated assignment
 */
export const startTimer = async (
  assignment: Assignment,
  userId?: string,
  userName?: string,
): Promise<Assignment> => {
  if (!assignment) {
    throw new Error('Cannot start timer - no assignment provided');
  }

  if (!userId || !userName) {
    throw new Error('Cannot start timer - no user ID or user name provided');
  }

  // Check if there's already an active time entry for this user
  const existingActiveEntry = getActiveTimeEntry(assignment, userId);
  if (existingActiveEntry) {
    console.log('Timer already running for this assignment and user');
    return assignment;
  }

  // Call the API to start time tracking
  const updatedAssignment = await apiStartTimeTracking(
    assignment.id,
    assignment.workspaceId,
    userId,
    userName,
  );

  return updatedAssignment;
};

/**
 * Stop a time tracking session for an assignment
 * @param assignment The assignment to stop tracking time for
 * @param userId The ID of the user tracking time
 * @param userName The name of the user tracking time
 * @returns A promise that resolves to the updated assignment
 */
export const stopTimer = async (
  assignment: Assignment,
  userId: string,
  userName: string,
): Promise<Assignment> => {
  if (!assignment) {
    throw new Error('Cannot stop timer - no assignment provided');
  }

  if (!userId) {
    throw new Error('Cannot stop timer - no user ID provided');
  }

  // Get the active time entry for this user
  const activeTimeEntry = getActiveTimeEntry(assignment, userId);
  if (!activeTimeEntry) {
    throw new Error('No active time entry found for this user');
  }

  // Call the API to stop time tracking with the timeEntryId
  const updatedAssignment = await apiStopTimeTracking(
    assignment.id,
    assignment.workspaceId,
    userId,
    userName,
    activeTimeEntry.id,
  );

  return updatedAssignment;
};
